
import React, { useCallback, useMemo } from 'react'
import { type NodeProps } from '@xyflow/react'
import { DataTable } from '@/components/canvas/nodes/variants/DataTable'
import { type GenieNodeState, NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'

export interface DataTableNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function DataTableNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as DataTableNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  const handleSelect = useCallback(() => {
    // Selection handled by React Flow
  }, [])

  const tableProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      width: NODE_DIMENSIONS.DataTable.width,
      height: NODE_DIMENSIONS.DataTable.height,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState,
    }),
    [id, genieState, selected, handleSelect, onHoverChange, onStateChange],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.DataTable

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <DataTable {...tableProps} />
    </>
  )
}

export const DataTableNode = React.memo(
  DataTableNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as DataTableNodeData
    const nextData = nextProps.data as unknown as DataTableNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.categories === nextData.state.categories &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

DataTableNode.displayName = 'DataTableNode'
